import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { softwareApi } from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import { apiErrorMessage, unwrapData } from '../../utils/responseHelpers';

const DetailField = ({ label, value }) => {
  const display =
    value === null || value === undefined || value === ''
      ? '—'
      : typeof value === 'string' && value.includes('_')
        ? formatEnumLabel(value)
        : String(value);

  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
        {display}
      </Typography>
    </Grid>
  );
};

const Section = ({ title, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryMain = theme.palette.primary.main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
        borderRadius: 2,
        bgcolor: isDark ? theme.palette.background.paper : '#fff',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        {children}
      </Grid>
    </Paper>
  );
};

const DataSourceDetailsViewPage = () => {
  const [searchParams] = useSearchParams();
  const softwareId = searchParams.get('softwareId');

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!softwareId) {
        setError('No software selected.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await softwareApi.get(softwareId);
        const data = unwrapData(response);
        if (!cancelled) {
          setDetail(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Failed to load data source details.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [softwareId]);

  const dataSources = detail?.dataSources ?? [];

  return (
    <Box m={{ xs: '12px', sm: '20px' }}>
      <Header title="Data Sources" />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : detail ? (
        <>
          <Section title="System details">
            <DetailField label="Software name" value={detail.softwareName} />
            <DetailField label="Internal ID" value={detail.internalReference} />
            <DetailField label="Deployment status" value={detail.deploymentStatus} />
            <DetailField label="Primary purpose" value={detail.primaryPurpose} />
            <DetailField
              label="Contract start date"
              value={detail.contractStartDate}
            />
            <DetailField label="Contract end date" value={detail.contractEndDate} />
          </Section>

          <Section title={`Data sources (${dataSources.length})`}>
            {dataSources.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  No data sources recorded for this system.
                </Typography>
              </Grid>
            ) : (
              dataSources.map((source, index) => (
                <Grid item xs={12} key={source.id ?? index}>
                  <Box
                    sx={{
                      pl: 1.5,
                      borderLeft: 2,
                      borderColor: 'divider',
                      mb: index < dataSources.length - 1 ? 1.5 : 0,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>
                      {source.sourceName || `Source ${index + 1}`}
                    </Typography>
                    <Grid container spacing={1.5}>
                      <DetailField label="Source name" value={source.sourceName} />
                      <DetailField label="Description" value={source.description} />
                    </Grid>
                  </Box>
                  {index < dataSources.length - 1 ? <Divider sx={{ my: 1.5 }} /> : null}
                </Grid>
              ))
            )}
          </Section>
        </>
      ) : null}
    </Box>
  );
};

export default DataSourceDetailsViewPage;
